import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

// Helpers de data (timezone local)
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const getPeriod = (key) => {
  const today = new Date();
  const to = toYMD(today);
  if (key === 'today') return { from: to, to, label: 'Hoje' };
  if (key === 'week') {
    const f = new Date(today); f.setDate(f.getDate() - 6);
    return { from: toYMD(f), to, label: 'Últimos 7 dias' };
  }
  if (key === 'month') {
    const f = new Date(today); f.setDate(f.getDate() - 29);
    return { from: toYMD(f), to, label: 'Últimos 30 dias' };
  }
  if (key === 'currentMonth') {
    const f = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toYMD(f), to, label: 'Mês atual' };
  }
  return { from: to, to, label: 'Hoje' };
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  // Range ativo
  const activeRange = useMemo(() => {
    if (periodKey === 'custom' && customRange.from && customRange.to) {
      return { from: customRange.from, to: customRange.to, label: `${customRange.from} → ${customRange.to}` };
    }
    return getPeriod(periodKey);
  }, [periodKey, customRange]);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const response = await api.get('/admin/dashboard/period', {
        params: {
          from: activeRange.from,
          to: activeRange.to,
          tzOffset: new Date().getTimezoneOffset(), // minutos para chegar em UTC
        },
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeRange.from, activeRange.to]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Auto-refresh 15s
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => fetchStats(true), 15000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchStats]);

  const formatCurrency = (n) => `R$ ${(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (n) => (n || 0).toLocaleString('pt-BR');
  const formatTime = (s) => {
    if (!s) return '';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return d.toLocaleDateString('pt-BR');
  };

  if (loading || !data) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .ad-loading { min-height: 100vh; background: #f5f5f5; display: flex; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; }
          .ad-loading-spinner { width: 50px; height: 50px; border: 4px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
          @keyframes spin { to { transform: rotate(360deg); } }
          .ad-loading p { color: #666; font-weight: 500; }
        `}</style>
        <div className="ad-loading">
          <div style={{ textAlign: 'center' }}>
            <div className="ad-loading-spinner"></div>
            <p>Carregando painel...</p>
          </div>
        </div>
      </>
    );
  }

  // Sparkline simples em SVG
  const series = data.series || [];
  const maxQty = Math.max(1, ...series.map(s => s.qty));
  const sparkPoints = series.length > 1
    ? series.map((s, i) => {
        const x = (i / (series.length - 1)) * 100;
        const y = 100 - (s.qty / maxQty) * 100;
        return `${x},${y}`;
      }).join(' ')
    : '';

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .ad *, .ad *::before, .ad *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .ad { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        
        /* ===== HEADER ===== */
        .ad-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .ad-header-logo { width: 40px; height: 40px; flex-shrink: 0; }
        .ad-header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .ad-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; }
        .ad-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .ad-header-back:hover { color: #fff; transform: translateX(-3px); }

        /* ===== MAIN ===== */
        .ad-main { max-width: 1200px; margin: 0 auto; padding: 35px 20px; }
        .ad-welcome {
          margin-bottom: 30px; padding: 32px 36px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
          border-radius: 20px;
          border: 1px solid rgba(255,153,0,0.20);
          box-shadow: 0 10px 40px rgba(0,0,0,0.20),
                      0 0 50px rgba(255,153,0,0.06) inset;
          position: relative; overflow: hidden;
          display: flex; align-items: center; justify-content: space-between; gap: 24px;
        }
        .ad-welcome::before {
          content: ''; position: absolute;
          top: -50%; right: -10%; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(255,153,0,0.18), transparent 70%);
          filter: blur(40px); pointer-events: none;
          animation: adWelcomeGlow 6s ease-in-out infinite;
        }
        .ad-welcome::after {
          content: ''; position: absolute;
          bottom: -40%; left: -5%; width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(255,102,0,0.12), transparent 70%);
          filter: blur(40px); pointer-events: none;
          animation: adWelcomeGlow 8s ease-in-out infinite reverse;
        }
        @keyframes adWelcomeGlow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -10px) scale(1.1); }
        }
        .ad-welcome-content { position: relative; z-index: 1; flex: 1; }
        .ad-welcome-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 12px; font-weight: 700;
          color: #fb923c; text-transform: uppercase; letter-spacing: 1.5px;
          padding: 6px 14px; border-radius: 25px;
          background: rgba(255,153,0,0.12); border: 1px solid rgba(255,153,0,0.30);
          margin-bottom: 14px;
        }
        .ad-welcome-eyebrow i { font-size: 10px; }
        .ad-welcome h2 {
          font-size: 36px; font-weight: 800; color: #fff;
          margin-bottom: 10px; letter-spacing: -0.5px; line-height: 1.1;
          background: linear-gradient(135deg, #fff 0%, #fdba74 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .ad-welcome p {
          color: rgba(255,255,255,0.75); font-size: 15px;
          font-weight: 500; line-height: 1.5;
        }
        .ad-welcome-icon {
          width: 90px; height: 90px;
          background: linear-gradient(135deg, #f90, #ff6600);
          border-radius: 22px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 12px 32px rgba(255,153,0,0.40),
                      inset 0 1px 0 rgba(255,255,255,0.30);
          flex-shrink: 0; position: relative; z-index: 1;
          transition: transform 0.4s ease;
        }
        .ad-welcome-icon:hover { transform: scale(1.06) rotate(-5deg); }
        .ad-welcome-icon i {
          font-size: 38px; color: #fff;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.25));
        }
        @media (max-width: 640px) {
          .ad-welcome { padding: 24px 22px; flex-direction: column; align-items: flex-start; }
          .ad-welcome-icon { width: 60px; height: 60px; }
          .ad-welcome-icon i { font-size: 26px; }
          .ad-welcome h2 { font-size: 26px; }
        }

        /* ===== PERIOD FILTER ===== */
        .ad-period {
          display: flex; align-items: center; gap: 10px;
          background: #fff; padding: 14px 18px; border-radius: 14px;
          margin-bottom: 22px; box-shadow: 0 4px 16px rgba(0,0,0,0.04);
          border: 1px solid rgba(0,0,0,0.04); flex-wrap: wrap;
        }
        .ad-period-label { font-size: 12px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-right: 4px; }
        .ad-period-btn {
          padding: 8px 16px; border-radius: 10px; border: 1px solid #e0e0e0;
          background: #fff; cursor: pointer; font-family: 'Lexend', sans-serif;
          font-size: 13px; font-weight: 600; color: #555; transition: all 0.2s;
        }
        .ad-period-btn:hover { border-color: #f90; color: #f90; }
        .ad-period-btn.active {
          background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border-color: #f90;
          box-shadow: 0 3px 10px rgba(255,153,0,0.30);
        }
        .ad-period-custom { display: flex; gap: 6px; align-items: center; margin-left: auto; }
        .ad-period-input {
          padding: 7px 10px; border: 1px solid #e0e0e0; border-radius: 8px;
          font-family: 'Lexend', sans-serif; font-size: 12px; color: #444;
        }
        .ad-period-input:focus { outline: none; border-color: #f90; }
        .ad-period-refresh {
          padding: 8px 14px; border-radius: 10px; border: 1px solid #e0e0e0;
          background: #fff; cursor: pointer; font-family: 'Lexend', sans-serif;
          font-size: 13px; font-weight: 600; color: #555; display: inline-flex; align-items: center; gap: 6px;
          transition: all 0.2s;
        }
        .ad-period-refresh:hover { border-color: #f90; color: #f90; }
        .ad-period-refresh.live { background: linear-gradient(135deg, #38a169, #2f855a); color: #fff; border-color: #38a169; }
        .ad-period-refresh.live i { animation: ad-pulse 1.5s ease-in-out infinite; }
        .ad-period-actions { display: flex; gap: 6px; align-items: center; }
        .ad-period-refresh.icon-only { padding: 8px 12px; }
        @keyframes ad-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }

        /* ===== KPI v3 — 4 grandes ===== */
        .ad-stats4 {
          display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr;
          gap: 16px; margin-bottom: 14px;
        }
        .ad-kpi {
          background: #fff; border-radius: 18px; padding: 22px 24px;
          display: flex; align-items: center; gap: 18px;
          box-shadow: 0 6px 24px rgba(15,23,42,0.06);
          border: 1px solid rgba(15,23,42,0.05);
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
          position: relative; overflow: hidden;
        }
        .ad-kpi::before {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 100%);
        }
        .ad-kpi:hover { transform: translateY(-3px); box-shadow: 0 12px 36px rgba(15,23,42,0.12); }
        .ad-kpi[role="button"] { cursor: pointer; }
        .ad-kpi-icon {
          width: 56px; height: 56px; border-radius: 16px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 24px;
          background: rgba(15,23,42,0.06); color: #475569;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.5);
        }
        .ad-kpi-body { flex: 1; min-width: 0; position: relative; z-index: 1; }
        .ad-kpi-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
        .ad-kpi-value { font-size: 30px; font-weight: 800; color: #0f172a; letter-spacing: -0.5px; line-height: 1.1; }
        .ad-kpi-sub { font-size: 12px; color: #94a3b8; margin-top: 6px; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .ad-kpi-go { font-size: 10px; opacity: 0.6; transition: transform 0.2s; }
        .ad-kpi[role="button"]:hover .ad-kpi-go { transform: translateX(4px); opacity: 1; }

        /* Variantes vibrantes */
        .ad-kpi-revenue {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%);
          border: 1px solid rgba(249,115,22,0.30);
        }
        .ad-kpi-revenue::before {
          background: radial-gradient(circle at 100% 0%, rgba(249,115,22,0.22), transparent 60%);
        }
        .ad-kpi-revenue .ad-kpi-icon {
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          box-shadow: 0 8px 20px rgba(249,115,22,0.40), inset 0 1px 0 rgba(255,255,255,0.3);
        }
        .ad-kpi-revenue .ad-kpi-label { color: rgba(255,255,255,0.65); }
        .ad-kpi-revenue .ad-kpi-value {
          background: linear-gradient(135deg, #fff 0%, #fed7aa 100%);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .ad-kpi-revenue .ad-kpi-sub { color: rgba(255,255,255,0.55); }

        .ad-kpi-credits .ad-kpi-icon { background: linear-gradient(135deg, #f59e0b, #d97706); color: #fff; box-shadow: 0 6px 18px rgba(245,158,11,0.35); }
        .ad-kpi-credits .ad-kpi-value { color: #d97706; }

        .ad-kpi-codes .ad-kpi-icon { background: linear-gradient(135deg, #3b82f6, #2563eb); color: #fff; box-shadow: 0 6px 18px rgba(59,130,246,0.35); }
        .ad-kpi-codes .ad-kpi-value { color: #2563eb; }

        .ad-kpi-ok .ad-kpi-icon { background: linear-gradient(135deg, #22c55e, #16a34a); color: #fff; box-shadow: 0 6px 18px rgba(34,197,94,0.35); }
        .ad-kpi-ok .ad-kpi-value { color: #16a34a; }

        .ad-kpi-alert {
          border: 1px solid rgba(239,68,68,0.50);
          background: linear-gradient(135deg, #fff 60%, rgba(239,68,68,0.10));
          animation: ad-pulse-alert 2s ease-in-out infinite;
        }
        .ad-kpi-alert .ad-kpi-icon { background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff; box-shadow: 0 6px 22px rgba(239,68,68,0.50); animation: ad-shake 1.6s ease-in-out infinite; }
        .ad-kpi-alert .ad-kpi-value { color: #dc2626; }
        .ad-kpi-alert .ad-kpi-sub { color: #ef4444; font-weight: 600; }
        @keyframes ad-pulse-alert { 0%,100% { box-shadow: 0 6px 24px rgba(239,68,68,0.15); } 50% { box-shadow: 0 6px 32px rgba(239,68,68,0.32); } }
        @keyframes ad-shake { 0%,100% { transform: rotate(0); } 20% { transform: rotate(-8deg); } 40% { transform: rotate(8deg); } 60% { transform: rotate(-4deg); } 80% { transform: rotate(4deg); } }

        /* Chips */
        .ad-chip {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600;
        }
        .ad-chip.green { background: rgba(34,197,94,0.18); color: #86efac; }
        .ad-chip.purple { background: rgba(168,85,247,0.20); color: #d8b4fe; }

        /* Mini stats bar */
        .ad-mini-stats {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
          background: #fff; border-radius: 14px; padding: 14px 18px;
          margin-bottom: 22px;
          box-shadow: 0 4px 18px rgba(15,23,42,0.05);
          border: 1px solid rgba(15,23,42,0.05);
        }
        .ad-mini { display: flex; align-items: center; gap: 12px; padding: 6px 10px; }
        .ad-mini > i { font-size: 22px; flex-shrink: 0; }
        .ad-mini > div { display: flex; flex-direction: column; min-width: 0; }
        .ad-mini-v { font-size: 18px; font-weight: 700; color: #0f172a; line-height: 1.1; }
        .ad-mini-l { font-size: 11px; color: #64748b; margin-top: 2px; }

        /* Legacy stats fallback (compat) */
        .ad-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
        .ad-stat-card.featured {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          color: #fff; border: 1px solid rgba(255,153,0,0.30);
        }
        .ad-stat-card.featured .ad-stat-label { color: rgba(255,255,255,0.65); }
        .ad-stat-card.featured .ad-stat-value { color: #fdba74; }
        .ad-stat-card.featured .ad-stat-icon { background: rgba(255,153,0,0.20); }
        .ad-stat-card.featured .ad-stat-icon i { color: #fdba74; }
        .ad-stat-card.alert {
          border: 1px solid rgba(229,62,62,0.40);
          background: linear-gradient(135deg, #fff 60%, rgba(229,62,62,0.08));
          animation: ad-alert-pulse 2.5s ease-in-out infinite;
        }
        .ad-stat-card.alert .ad-stat-value { color: #e53e3e; }
        .ad-stat-card.alert .ad-stat-icon { background: rgba(229,62,62,0.15); }
        .ad-stat-card.alert .ad-stat-icon i { color: #e53e3e; }
        @keyframes ad-alert-pulse {
          0%,100% { box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
          50% { box-shadow: 0 4px 25px rgba(229,62,62,0.20); }
        }
        .ad-stat-card {
          background: #fff; padding: 24px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          transition: all 0.3s; border: 1px solid rgba(0,0,0,0.04);
        }
        .ad-stat-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(0,0,0,0.1); }
        .ad-stat-header { display: flex; justify-content: space-between; align-items: flex-start; }
        .ad-stat-label { font-size: 12px; color: #999; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .ad-stat-value { font-size: 28px; font-weight: 700; color: #f90; }
        .ad-stat-sub { font-size: 13px; color: #22c55e; font-weight: 600; margin-top: 4px; }
        .ad-stat-sub.pending { color: #f59e0b; }
        .ad-stat-icon {
          width: 50px; height: 50px; background: linear-gradient(135deg, #fff5e6, #ffedd5);
          border-radius: 12px; display: flex; align-items: center; justify-content: center;
        }
        .ad-stat-icon i { font-size: 20px; color: #f90; }

        /* ===== ACTIONS ===== */
        .ad-actions { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
        .ad-action-card {
          background: linear-gradient(135deg, #1a1a2e, #16213e); padding: 25px;
          border-radius: 16px; cursor: pointer; transition: all 0.3s; position: relative; overflow: hidden;
          box-shadow: 0 6px 25px rgba(0,0,0,0.15); border: 1px solid rgba(255,153,0,0.1); text-align: left;
        }
        .ad-action-card:hover { transform: translateY(-5px); box-shadow: 0 10px 35px rgba(0,0,0,0.2); border-color: rgba(255,153,0,0.3); }
        .ad-action-card::before {
          content: ''; position: absolute; top: -50%; right: -20%;
          width: 200px; height: 200px; border-radius: 50%;
          background: radial-gradient(circle, rgba(255,153,0,0.15), transparent 70%);
        }
        .ad-action-content { position: relative; z-index: 1; }
        .ad-action-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .ad-action-icon {
          width: 45px; height: 45px; background: rgba(255,153,0,0.2); border-radius: 12px;
          display: flex; align-items: center; justify-content: center; transition: transform 0.3s;
        }
        .ad-action-card:hover .ad-action-icon { transform: scale(1.1); }
        .ad-action-icon i { font-size: 18px; color: #f90; }
        .ad-action-title { font-size: 18px; font-weight: 700; color: #fff; }
        .ad-action-desc { color: rgba(255,255,255,0.7); font-size: 13px; }
        .ad-action-icon-shopify { background: rgba(149,191,71,0.20) !important; }
        .ad-action-icon-shopify i { color: #95BF47 !important; font-size: 20px; }
        .ad-action-shopify:hover { border-color: rgba(149,191,71,0.40); }
        .ad-action-shopify::before { background: radial-gradient(circle, rgba(149,191,71,0.18), transparent 70%) !important; }

        /* PANELS */
        .ad-double { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 22px; }
        .ad-panel {
          background: #fff; border-radius: 16px; padding: 22px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
        }
        .ad-panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
        .ad-panel-head h3 { font-size: 15px; color: #333; display: inline-flex; align-items: center; gap: 8px; font-weight: 700; }
        .ad-panel-head h3 i { color: #f90; }
        .ad-panel-meta { font-size: 12px; color: #999; }
        .ad-panel-link { background: none; border: none; color: #f90; font-weight: 600; font-size: 12px; cursor: pointer; font-family: inherit; }
        .ad-panel-link:hover { color: #ff6600; }
        .ad-panel-empty { padding: 30px 0; color: #999; text-align: center; font-size: 13px; }
        .ad-spark { width: 100%; height: 110px; display: block; margin-bottom: 8px; }
        .ad-spark-meta { display: flex; gap: 18px; font-size: 12px; color: #888; padding-top: 8px; border-top: 1px dashed #eee; flex-wrap: wrap; }
        .ad-spark-meta strong { color: #333; }
        .ad-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
        .ad-list li {
          display: flex; align-items: center; gap: 12px; padding: 10px 12px;
          border-radius: 10px; cursor: pointer; transition: background 0.15s;
        }
        .ad-list li:hover { background: #fafafa; }
        .ad-list-icon {
          width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 12px;
        }
        .ad-list-icon.pending { background: rgba(237,137,54,0.15); color: #d97706; }
        .ad-list-icon.ok { background: rgba(56,161,105,0.15); color: #38a169; }
        .ad-list-icon.error { background: rgba(229,62,62,0.15); color: #e53e3e; }
        .ad-list-text { flex: 1; font-size: 13px; color: #444; min-width: 0; }
        .ad-list-text strong { color: #222; font-weight: 600; }
        .ad-list-text small { display: block; color: #999; font-size: 11px; margin-top: 2px; }
        .ad-list.compact li { padding: 8px 12px; }

        @media (max-width: 1280px) {
          .ad-stats { grid-template-columns: repeat(2, 1fr); }
          .ad-stats4 { grid-template-columns: repeat(2, 1fr); }
          .ad-mini-stats { grid-template-columns: repeat(2, 1fr); }
          .ad-actions { grid-template-columns: repeat(2, 1fr); }
          .ad-double { grid-template-columns: 1fr; }
        }
        @media (max-width: 540px) {
          .ad-stats4 { grid-template-columns: 1fr; }
          .ad-mini-stats { grid-template-columns: 1fr; }
          .ad-kpi-value { font-size: 26px; }
        }
        @media (max-width: 640px) {
          .ad-header { padding: 15px 20px; }
          .ad-header-title { font-size: 18px; }
          .ad-stats { grid-template-columns: 1fr; }
          .ad-actions { grid-template-columns: 1fr; }
          .ad-main { padding: 25px 15px; }
        }
      `}</style>

      <div className="ad">
        {/* Header antigo removido — título e "voltar" já ficam na sidebar (AdminLayout) */}

        {/* Main */}
        <main className="ad-main">
          {/* Filtro de período */}
          <div className="ad-period">
            <span className="ad-period-label">Período:</span>
            {[
              { k: 'today', l: 'Hoje' },
              { k: 'week', l: '7 dias' },
              { k: 'month', l: '30 dias' },
              { k: 'currentMonth', l: 'Mês atual' },
              { k: 'custom', l: 'Personalizado' },
            ].map(p => (
              <button
                key={p.k}
                className={`ad-period-btn ${periodKey === p.k ? 'active' : ''}`}
                onClick={() => setPeriodKey(p.k)}
              >{p.l}</button>
            ))}
            {periodKey === 'custom' && (
              <div className="ad-period-custom">
                <input
                  type="date" className="ad-period-input"
                  value={customRange.from}
                  onChange={(e) => setCustomRange(r => ({ ...r, from: e.target.value }))}
                />
                <span style={{ color: '#999' }}>→</span>
                <input
                  type="date" className="ad-period-input"
                  value={customRange.to}
                  onChange={(e) => setCustomRange(r => ({ ...r, to: e.target.value }))}
                />
              </div>
            )}
            <div className="ad-period-actions" style={{ marginLeft: periodKey === 'custom' ? 0 : 'auto' }}>
              <button
                className={`ad-period-refresh ${autoRefresh ? 'live' : ''}`}
                onClick={() => setAutoRefresh(a => !a)}
                title="Atualização automática a cada 15 segundos"
              >
                <i className={`fas ${autoRefresh ? 'fa-circle' : 'fa-bolt'}`}></i>
                {autoRefresh ? 'AO VIVO' : 'Atualizar auto'}
              </button>
              <button
                className="ad-period-refresh icon-only"
                onClick={() => fetchStats(true)}
                disabled={refreshing}
                title="Atualizar agora"
              >
                <i className={`fas fa-redo ${refreshing ? 'fa-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          {/* Stats principais (4 grandes) */}
          <div className="ad-stats4">
            <div className="ad-kpi ad-kpi-revenue">
              <div className="ad-kpi-icon"><i className="fas fa-dollar-sign"></i></div>
              <div className="ad-kpi-body">
                <div className="ad-kpi-label">Receita — {activeRange.label}</div>
                <div className="ad-kpi-value">{formatCurrency(data.sales.revenue)}</div>
                <div className="ad-kpi-sub">
                  <span className="ad-chip green">
                    <i className="fas fa-check"></i> {formatNumber(data.sales.approved.count)} aprovada{data.sales.approved.count !== 1 ? 's' : ''}
                  </span>
                  {data.sales.manual.qty > 0 && (
                    <span className="ad-chip purple">
                      <i className="fas fa-plus"></i> {formatNumber(data.sales.manual.qty)} manual
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="ad-kpi ad-kpi-credits">
              <div className="ad-kpi-icon"><i className="fas fa-coins"></i></div>
              <div className="ad-kpi-body">
                <div className="ad-kpi-label">Créditos vendidos</div>
                <div className="ad-kpi-value">{formatNumber(data.sales.approved.qty)}</div>
                <div className="ad-kpi-sub">{formatNumber(data.totals.circulatingCredits)} circulando</div>
              </div>
            </div>

            <div className="ad-kpi ad-kpi-codes" onClick={() => navigate('/admin/codes')} role="button">
              <div className="ad-kpi-icon"><i className="fas fa-qrcode"></i></div>
              <div className="ad-kpi-body">
                <div className="ad-kpi-label">Códigos gerados <i className="fas fa-arrow-right ad-kpi-go"></i></div>
                <div className="ad-kpi-value">{formatNumber(data.codesGenerated)}</div>
                <div className="ad-kpi-sub">{formatNumber(data.totals.totalCodes)} no total</div>
              </div>
            </div>

            {/* Auditoria de créditos: reconcilia saldo x pedidos x códigos.
                Verde = toda entrada de crédito tem origem (compra ou manual).
                Vermelho = crédito apareceu sem origem identificável. */}
            {(() => {
              const audit = data.creditsAudit || { orphanCredits: 0, anomalyUsers: 0, anomalies: [] };
              const hasAnomaly = audit.anomalyUsers > 0;
              return (
                <div
                  className={`ad-kpi ${hasAnomaly ? 'ad-kpi-alert' : 'ad-kpi-ok'}`}
                  onClick={() => hasAnomaly && setShowAudit((v) => !v)}
                  role={hasAnomaly ? 'button' : undefined}
                  style={hasAnomaly ? { cursor: 'pointer' } : undefined}
                >
                  <div className="ad-kpi-icon">
                    <i className={`fas ${hasAnomaly ? 'fa-exclamation-triangle' : 'fa-shield-alt'}`}></i>
                  </div>
                  <div className="ad-kpi-body">
                    <div className="ad-kpi-label">{hasAnomaly ? 'Créditos sem origem' : 'Auditoria de créditos'}</div>
                    <div className="ad-kpi-value">{formatNumber(hasAnomaly ? audit.orphanCredits : 0)}</div>
                    <div className="ad-kpi-sub">
                      {hasAnomaly
                        ? `${audit.anomalyUsers} usuário(s) — ver detalhes ${showAudit ? '▲' : '→'}`
                        : 'Comprados x circulando conferem'}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Detalhe das anomalias de crédito (expande ao clicar no card) */}
          {showAudit && data.creditsAudit && data.creditsAudit.anomalies.length > 0 && (
            <div className="ad-panel" style={{ marginBottom: 20 }}>
              <div className="ad-panel-head">
                <h3><i className="fas fa-exclamation-triangle" style={{ color: '#ef4444' }}></i> Auditoria de créditos — anomalias</h3>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <button
                    className="ad-panel-link"
                    disabled={acknowledging}
                    onClick={async () => {
                      if (!window.confirm('Marcar essas diferenças como conferidas? O card volta a ficar verde e só alertará anomalias NOVAS.')) return;
                      setAcknowledging(true);
                      try {
                        await api.post('/admin/credits-audit/acknowledge');
                        setShowAudit(false);
                        fetchStats(true);
                      } catch (e) {
                        alert('Falha ao registrar. Tente novamente.');
                      } finally {
                        setAcknowledging(false);
                      }
                    }}
                  >
                    <i className="fas fa-check"></i> {acknowledging ? 'Registrando...' : 'Marcar como conferido'}
                  </button>
                  <button className="ad-panel-link" onClick={() => setShowAudit(false)}>Fechar ✕</button>
                </div>
              </div>
              <ul className="ad-list">
                {data.creditsAudit.anomalies.map((a) => (
                  <li key={a.userId}>
                    <div className="ad-list-icon" style={{ background: a.diff > 0 ? 'rgba(239,68,68,.15)' : 'rgba(245,158,11,.15)' }}>
                      <i className={`fas ${a.diff > 0 ? 'fa-plus-circle' : 'fa-minus-circle'}`} style={{ color: a.diff > 0 ? '#ef4444' : '#f59e0b' }}></i>
                    </div>
                    <div className="ad-list-text">
                      <strong>{a.username}</strong> — {a.email}
                      <small>saldo: {formatNumber(a.credits)} · esperado: {formatNumber(a.expected)} · diferença: {a.diff > 0 ? '+' : ''}{formatNumber(a.diff)}</small>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Mini-stats em barra */}
          <div className="ad-mini-stats">
            <div className="ad-mini">
              <i className="fas fa-users" style={{ color: '#3b82f6' }}></i>
              <div>
                <span className="ad-mini-v">{formatNumber(data.activeUsers)}</span>
                <span className="ad-mini-l">ativos / {formatNumber(data.totals.totalUsers)}</span>
              </div>
            </div>
            <div className="ad-mini">
              <i className="fas fa-user-plus" style={{ color: '#22c55e' }}></i>
              <div>
                <span className="ad-mini-v">{formatNumber(data.newUsers)}</span>
                <span className="ad-mini-l">novos cadastros</span>
              </div>
            </div>
            <div className="ad-mini">
              <i className="fas fa-sign-in-alt" style={{ color: '#a855f7' }}></i>
              <div>
                <span className="ad-mini-v">{formatNumber(data.logins)}</span>
                <span className="ad-mini-l">logins no período</span>
              </div>
            </div>
            <div className="ad-mini">
              <i className="fas fa-wallet" style={{ color: '#f97316' }}></i>
              <div>
                <span className="ad-mini-v">{formatNumber(data.totals.circulatingCredits)}</span>
                <span className="ad-mini-l">créditos circulando</span>
              </div>
            </div>
          </div>

          {/* Sparkline + Pendentes recentes */}
          <div className="ad-double">
            <div className="ad-panel">
              <div className="ad-panel-head">
                <h3><i className="fas fa-chart-area"></i> Vendas por dia</h3>
                <span className="ad-panel-meta">{activeRange.label}</span>
              </div>
              {series.length === 0 ? (
                <div className="ad-panel-empty">Sem vendas no período</div>
              ) : (
                <>
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="ad-spark">
                    <defs>
                      <linearGradient id="adSparkFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f90" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#f90" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {series.length > 1 && (
                      <>
                        <polygon points={`0,100 ${sparkPoints} 100,100`} fill="url(#adSparkFill)" />
                        <polyline points={sparkPoints} fill="none" stroke="#f90" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                      </>
                    )}
                  </svg>
                  <div className="ad-spark-meta">
                    <span><strong>{formatNumber(series.reduce((s, x) => s + x.qty, 0))}</strong> créditos</span>
                    <span><strong>{series.length}</strong> dia{series.length !== 1 ? 's' : ''}</span>
                    <span>máx: <strong>{formatNumber(maxQty)}</strong>/dia</span>
                  </div>
                </>
              )}
            </div>
            <div className="ad-panel">
              <div className="ad-panel-head">
                <h3><i className="fas fa-clock"></i> Pedidos pendentes</h3>
                {data.recentPending.length > 0 && (
                  <button className="ad-panel-link" onClick={() => navigate('/admin/sales')}>Ver todos →</button>
                )}
              </div>
              {data.recentPending.length === 0 ? (
                <div className="ad-panel-empty"><i className="fas fa-check-circle" style={{ color: '#22c55e' }}></i> Nenhum pendente</div>
              ) : (
                <ul className="ad-list">
                  {data.recentPending.map(p => (
                    <li key={p.id} onClick={() => navigate('/admin/sales')}>
                      <div className="ad-list-icon pending"><i className="fas fa-shopping-cart"></i></div>
                      <div className="ad-list-text">
                        <strong>{p.username}</strong> — {p.quantity} créditos
                        <small>{formatTime(p.created_at)}</small>
                      </div>
                      <i className="fas fa-chevron-right" style={{ color: '#bbb' }}></i>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Cards de atalho (Usuários, Vendas, Relatórios, Configurações,
              Registro de Auditoria, Integrações Shopify) removidos — agora
              vivem como itens de navegação na sidebar (AdminLayout), sempre
              visíveis em qualquer tela admin, sem duplicar aqui. */}
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;
